import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import ButtonTooltip from "components/Common/ButtonTooltip"
import DeleteModal from "components/Common/DeleteModal"
import ConfirmModal from "components/Common/ConfirmModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import ToggleSelectButton from "components/Common/ToggleSelectButton"
import InputField from "components/Common/InputField"
import { ACCESS } from "constants/accessPermissions"

import { useListParams } from "hooks/useListParams"
import {
  useAccountRtgsPayments,
  useAccountRtgsPayment,
  useDeleteAccountRtgsPayment,
  useCompleteAccountRtgsPayment,
} from "hooks/useAccountRtgsPayment"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { downloadRtgsPdf, downloadRtgsPrint } from "api/accountRtgsPaymentApi"

import AccountRtgsPaymentAddEdit from "./AccountRtgsPaymentAddEdit"
import AccountRtgsPaymentView from "./AccountRtgsPaymentView"

const STATUS_COLORS = {
  pending: "warning",
  completed: "success",
  cancelled: "secondary",
}

const AccountRtgsPaymentList = () => {
  document.title = "RTGS Payments"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedEditId, setSelectedEditId] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [completeModal, setCompleteModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)
  const [printLoadingId, setPrintLoadingId] = useState(null)
  const [pdfModal, setPdfModal] = useState(false)
  const [pdfTargetId, setPdfTargetId] = useState(null)
  const [includePart1, setIncludePart1] = useState(true)
  const [includePart2, setIncludePart2] = useState(true)
  const [includeAmount, setIncludeAmount] = useState(true)
  const [printDateEnabled, setPrintDateEnabled] = useState(true)
  const [printDateValue, setPrintDateValue] = useState("")
  const [statusFilter, setStatusFilter] = useState("")

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({
    page: 1,
    search: "",
  })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(statusFilter && { status: statusFilter }),
    }
  }, [params, statusFilter])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountRtgsPayments(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountRtgsPayment(selectedViewId, viewModal)
  const { data: editResponse, isLoading: isEditLoading } = useAccountRtgsPayment(selectedEditId, modal && isEdit)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountRtgsPayment()
  const { mutateAsync: completeItem, isPending: completeLoading } = useCompleteAccountRtgsPayment()

  const handleComplete = async id => {
    try {
      const res = await completeItem(id)
      toast.success(res?.message ?? "RTGS payment completed.")
      refetch()
    } catch {
      toast.error("Failed to complete RTGS payment.")
    }
  }

  const handleCompleteClick = item => {
    setSelectedData(item)
    setCompleteModal(true)
  }

  const handleCompleteConfirm = async () => {
    if (!selectedData?.id) return
    try {
      const res = await completeItem(selectedData.id)
      toast.success(res?.message ?? "RTGS payment marked as completed.")
      setCompleteModal(false)
      setSelectedData(null)
      refetch()
    } catch {
      toast.error("Failed to complete RTGS payment.")
    }
  }

  const handleViewPdf = async id => {
    if (!id) return
    try {
      setPdfLoadingId(id)
      await downloadRtgsPdf(id, { withPad: true })
    } catch {
      toast.error("Failed to download PDF.")
    } finally {
      setPdfLoadingId(null)
    }
  }

  const handlePdfClick = id => {
    setPdfTargetId(id)
    setIncludePart1(true)
    setIncludePart2(true)
    setIncludeAmount(true)
    setPrintDateEnabled(true)
    setPrintDateValue(new Date().toISOString().slice(0, 10))
    setPdfModal(true)
  }

  const handlePdf = async withPad => {
    const id = pdfTargetId
    if (!id) return
    if (!includePart1 && !includePart2 && !includeAmount) {
      toast.error("Select at least one section.")
      return
    }
    try {
      setPdfLoadingId(id)
      await downloadRtgsPdf(id, {
        withPad,
        part1: includePart1,
        part2: includePart2,
        amount: includeAmount,
        printDate: printDateEnabled ? printDateValue : "",
      })
      setPdfModal(false)
    } catch {
      toast.error("Failed to download PDF.")
    } finally {
      setPdfLoadingId(null)
    }
  }

  const handlePrint = async id => {
    try {
      setPrintLoadingId(id)
      await downloadRtgsPrint(id)
    } catch {
      toast.error("Failed to download print form.")
    } finally {
      setPrintLoadingId(null)
    }
  }

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedEditId(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedEditId(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedEditId(item?.id || null)
    setIsEdit(true)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "RTGS payment deleted.")
      setDeleteModal(false)
      setSelectedData(null)
      refetch()
    } catch {
      toast.error("Failed to delete RTGS payment.")
    }
  }

  const handleFilterReset = () => {
    setStatusFilter("")
  }

  const hasStatusFilter = !!statusFilter

  const columns = useMemo(
    () => [
      {
        header: "RTGS No",
        meta: { style: { minWidth: "160px" } },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <span className="font-monospace fw-semibold">{item.rtgs_no || "-"}</span>
              <div className="d-sm-none small text-muted mt-1">
                {item.date ? formatReadableDate(item.date) : "-"}
              </div>
              <div className="d-sm-none mt-1">
                <div className="fw-semibold small">{item.beneficiary_name || "-"}</div>
                {item.beneficiary_bank_name ? (
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    {item.beneficiary_bank_name}
                    {item.beneficiary_branch_name ? ` — ${item.beneficiary_branch_name}` : ""}
                  </div>
                ) : null}
              </div>
              <div className="d-sm-none mt-1 d-flex align-items-center gap-2 flex-wrap">
                <Badge color="light" className="text-dark">
                  {formatCurrency(item.amount || 0, "BDT")}
                </Badge>
                <Badge color={STATUS_COLORS[item.status] || "secondary"} pill>
                  {item.status || "pending"}
                </Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Date",
        meta: { style: { width: "12%" }, className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
            {row.original.date ? formatReadableDate(row.original.date) : "-"}
          </div>
        ),
      },
      {
        header: "Beneficiary",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="fw-semibold">{item.beneficiary_name || "-"}</div>
              <div className="text-muted small">
                {item.beneficiary_bank_name || ""}
                {item.beneficiary_branch_name ? ` — ${item.beneficiary_branch_name}` : ""}
              </div>
              <div className="d-md-none mt-1">
                <Badge color="light" className="text-dark">
                  {formatCurrency(item.amount || 0, "BDT")}
                </Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Amount",
        meta: { style: { width: "14%" }, className: "d-none d-md-table-cell" },
        cell: ({ row }) => (
          <Badge color="light" className="text-dark">
            {formatCurrency(row.original.amount || 0, "BDT")}
          </Badge>
        ),
      },
      {
        header: "Status",
        meta: { style: { width: "10%" }, className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <Badge color={STATUS_COLORS[row.original.status] || "secondary"} pill>
            {row.original.status || "pending"}
          </Badge>
        ),
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => {
          const item = row.original
          const id = item.id
          const status = item.status
          return (
            <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
              <li>
                <ButtonTooltip
                  type="view"
                  btnId={`view-rtgs-${id}`}
                  onClick={() => handleViewClick(item)}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="pdf"
                  btnId={`pdf-rtgs-${id}`}
                  loading={pdfLoadingId === id}
                  onClick={() => handlePdfClick(id)}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="download"
                  btnId={`print-rtgs-${id}`}
                  loading={printLoadingId === id}
                  onClick={() => handlePrint(id)}
                />
              </li>
              <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.COMPLETE]}>
                {status === "pending" && (
                  <li>
                    <ButtonTooltip
                      type="receipt"
                      btnId={`complete-rtgs-${id}`}
                      loading={completeLoading}
                      onClick={() => handleCompleteClick(item)}
                    />
                  </li>
                )}
              </CanView>
              <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.EDIT]}>
                <li>
                  <ButtonTooltip
                    type="edit"
                    btnId={`edit-rtgs-${id}`}
                    onClick={() => handleEditClick(item)}
                  />
                </li>
              </CanView>
              <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`delete-rtgs-${id}`}
                    loading={deleteLoading}
                    onClick={() => handleDeleteClick(item)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [pdfLoadingId, printLoadingId, completeLoading, deleteLoading]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="RTGS Payments" breadcrumbItem="RTGS Payment List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total <span className="badge badge-soft-info">{paginationInfo?.total ?? 0}</span>
          </h5>
        </Col>
        <Col xs={6} className="d-flex justify-content-end align-items-center gap-1">
          <ButtonTooltip
            type="refresh"
            btnId="refreshRtgsList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>
              <i className="bx bx-plus" />
              <span className="d-none d-sm-inline ms-1">Add New</span>
            </Button>
          </CanView>
          {hasActiveFilters && (
            <Button color="danger" onClick={() => { handleFilterReset(); resetParams() }}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3 gy-2 align-items-center">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search RTGS, beneficiary, remitter..."
          />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <select
            className="form-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={params.per_page}
        getRowKey={row => row.id}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => {
          setDeleteModal(false)
          setSelectedData(null)
        }}
      />

      <ConfirmModal
        isOpen={completeModal}
        toggle={() => {
          setCompleteModal(false)
          setSelectedData(null)
        }}
        title="Mark RTGS Payment as Completed"
        message={
          selectedData
            ? `Are you sure you want to mark RTGS payment ${selectedData.rtgs_no || `#${selectedData.id}`} as completed? This will post a debit entry to the ledger and cannot be undone from the list.`
            : ""
        }
        confirmLabel="Mark Complete"
        btnColor="success"
        icon="mdi mdi-check-circle-outline"
        onConfirm={handleCompleteConfirm}
        isConfirming={completeLoading}
      />

      <Modal
        isOpen={pdfModal}
        toggle={() => setPdfModal(prev => !prev)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setPdfModal(false)}>Download RTGS PDF</ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Choose which sections to include, then pick an output format.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <ToggleSelectButton
              active={includePart1}
              onToggle={setIncludePart1}
              label="Part 1 — Sender Information"
            />
            <ToggleSelectButton
              active={includePart2}
              onToggle={setIncludePart2}
              label="Part 2 — Beneficiary Information"
            />
            <ToggleSelectButton
              active={includeAmount}
              onToggle={setIncludeAmount}
              label="Amount + In Words"
            />
          </div>
          {!includePart1 && !includePart2 && !includeAmount && (
            <div className="text-danger small mt-2">Select at least one section.</div>
          )}

          <hr className="my-3" />

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <ToggleSelectButton
              active={printDateEnabled}
              onToggle={setPrintDateEnabled}
              label="Print Date"
            />
            {printDateEnabled && (
              <div style={{ maxWidth: "180px" }}>
                <InputField
                  type="date"
                  name="rtgs-print-date"
                  value={printDateValue}
                  onChange={e => setPrintDateValue(e.target.value)}
                />
              </div>
            )}
          </div>
          {!printDateEnabled && (
            <div className="text-muted small mt-2">Date line will be left blank.</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPdfModal(false)}>
            Cancel
          </Button>
          <Button
            color="outline-primary"
            disabled={
              (!includePart1 && !includePart2 && !includeAmount) ||
              pdfLoadingId === pdfTargetId
            }
            onClick={() => handlePdf(false)}
          >
            Text Only (no pad)
          </Button>
          <Button
            color="primary"
            disabled={
              (!includePart1 && !includePart2 && !includeAmount) ||
              pdfLoadingId === pdfTargetId
            }
            onClick={() => handlePdf(true)}
          >
            With Bank Pad
          </Button>
        </ModalFooter>
      </Modal>

      <AccountRtgsPaymentView
        modal={viewModal}
        toggleModal={toggleViewModal}
        rtgsPayment={viewResponse?.data}
        loading={isViewLoading}
        onComplete={handleComplete}
        onPdf={handleViewPdf}
        pdfLoadingId={pdfLoadingId}
      />

      <AccountRtgsPaymentAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={editResponse?.data}
        isFetchingEdit={isEditLoading}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />
    </PageWrapper>
  )
}

export default AccountRtgsPaymentList
